import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { PasswordStrength } from '../components/PasswordStrength';
import { LegalDocumentModal } from '../components/LegalDocumentModal';
import { TERMS_VERSION } from '../utils/legal';
import { useLanguages, flagFor } from '../context/LanguagesContext';

const API = process.env.REACT_APP_BACKEND_URL;

export const RegisterPage = () => {
  const { t, i18n } = useTranslation();
  const { branding } = useBranding();
  const { items: languages, default: defaultLang } = useLanguages();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [newsletter, setNewsletter] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // Language selected at signup. Initial value follows the active i18n
  // language (which itself defaults to the platform default via the
  // LanguagesProvider) so the selector mirrors the current header pick.
  const [uiLanguage, setUiLanguage] = useState(i18n.language || defaultLang || 'fr');
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleRegister = async (e) => {
    e.preventDefault();
    if (fullName.trim().length < 2) {
      toast.error(t('auth.nameRequired'));
      return;
    }
    if (password !== confirmPassword) {
      toast.error(t('auth.passwordMismatch'));
      return;
    }
    if (!acceptedTerms) {
      toast.error(t('auth.termsRequired'));
      return;
    }
    setLoading(true);
    const result = await register(
      email, password, fullName.trim(), phone || null, null, newsletter, TERMS_VERSION,
      { ui_language: uiLanguage }
    );
    setLoading(false);

    if (result.success) {
      toast.success('Account created successfully!');
      navigate('/');
    } else {
      toast.error(result.error);
    }
  };

  const handleGoogleSignup = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Link to="/" className="inline-flex flex-col items-center" data-testid="register-logo">
            <img
              src={branding?.logo_path ? `${API}/api/files/${branding.logo_path}` : '/ato-logo.png'}
              alt={branding?.platform_name || 'ATÔ'}
              className="w-[160px] md:w-[200px] h-auto object-contain"
            />
          </Link>
        </div>

        <div className="bg-white border border-border rounded-2xl p-8 shadow-sm">
          <h2 className="text-lg md:text-xl font-medium text-text_strong tracking-wide mb-2" data-testid="register-title">
            {t('auth.createAccount')}
          </h2>
          <p className="text-text_muted mb-6">{t('auth.joinMarketplace')}</p>

          <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
            {/* Language picker — sets UI language immediately and is sent
                to the backend on submit (saved as user.ui_language). */}
            <div data-testid="register-language-section">
              <Label htmlFor="register-language">{t('auth.chooseLanguage')}</Label>
              <select
                id="register-language"
                value={uiLanguage}
                onChange={(e) => {
                  const code = e.target.value;
                  setUiLanguage(code);
                  i18n.changeLanguage(code);
                  localStorage.setItem('language', code);
                }}
                className="mt-1 w-full rounded-md border border-border bg-white px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
                data-testid="register-language-select"
              >
                {languages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {flagFor(lang.code)} {lang.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="full-name">{t('auth.fullName')} *</Label>
              <Input
                id="full-name"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder={t('auth.fullNamePlaceholder')}
                required
                minLength={2}
                className="border border-border focus:border-primary focus:ring-1 focus:ring-primary"
                data-testid="full-name-input"
              />
            </div>
            <div>
              <Label htmlFor="email">{t('auth.email')} *</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="border border-border focus:border-primary focus:ring-1 focus:ring-primary"
                data-testid="email-input"
              />
            </div>
            <div>
              <Label htmlFor="phone">{t('auth.phoneOptional')}</Label>
              <PhoneInput
                international
                defaultCountry="MG"
                value={phone}
                onChange={setPhone}
                className="phone-input-wrapper"
                data-testid="phone-input"
              />
            </div>
            <div>
              <Label htmlFor="password">{t('auth.password')} *</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="border border-border focus:border-primary focus:ring-1 focus:ring-primary pr-11"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-text_muted hover:text-text_main transition-colors"
                  data-testid="password-toggle-button"
                >
                  {showPassword ? <EyeSlash size={20} weight="regular" /> : <Eye size={20} weight="regular" />}
                </button>
              </div>
              <PasswordStrength password={password} />
            </div>
            <div>
              <Label htmlFor="confirmPassword">{t('auth.confirmPassword')} *</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  className="border border-border focus:border-primary focus:ring-1 focus:ring-primary pr-11"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(v => !v)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-text_muted hover:text-text_main transition-colors"
                  data-testid="confirm-password-toggle-button"
                >
                  {showConfirmPassword ? <EyeSlash size={20} weight="regular" /> : <Eye size={20} weight="regular" />}
                </button>
              </div>
            </div>
            <div className="flex items-start gap-3 py-2">
              <input
                type="checkbox"
                id="newsletter"
                checked={newsletter}
                onChange={(e) => setNewsletter(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary"
                data-testid="newsletter-checkbox"
              />
              <div>
                <label htmlFor="newsletter" className="text-sm font-medium text-text_main cursor-pointer">
                  {t('auth.newsletterLabel')}
                </label>
                <p className="text-xs text-text_muted mt-0.5">
                  {t('auth.newsletterDesc')}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 pt-1" data-testid="terms-acceptance">
              <input
                type="checkbox"
                id="acceptTerms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary flex-shrink-0"
                data-testid="accept-terms-checkbox"
              />
              <label htmlFor="acceptTerms" className="text-sm text-text_main cursor-pointer leading-relaxed">
                {t('auth.acceptTerms')}{' '}
                <button
                  type="button"
                  onClick={() => setTermsOpen(true)}
                  className="text-primary hover:underline font-medium"
                  data-testid="open-terms-modal"
                >
                  {t('auth.termsAndConditions')}
                </button>
                {' '}{t('auth.andPrivacyPolicy')}{' '}
                <button
                  type="button"
                  onClick={() => setPrivacyOpen(true)}
                  className="text-primary hover:underline font-medium"
                  data-testid="open-privacy-modal"
                >
                  {t('auth.privacyPolicy')}
                </button>
              </label>
            </div>

            <Button
              type="submit"
              disabled={loading}
              variant="primary"
              className="w-full font-semibold tracking-wide active:scale-95 py-3"
              data-testid="register-submit-button"
            >
              {loading ? t('common.loading') : t('auth.signUp')}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-text_muted">Or</span>
            </div>
          </div>

          <Button
            type="button"
            onClick={handleGoogleSignup}
            variant="outline"
            className="w-full"
            data-testid="google-signup-button"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t('auth.continueWithGoogle')}
          </Button>

          <p className="text-center text-sm text-text_muted mt-6">
            {t('auth.alreadyHaveAccount')}{' '}
            <Link to="/login" className="text-primary hover:underline font-medium" data-testid="login-link">
              {t('auth.signIn')}
            </Link>
          </p>
        </div>
      </div>

      <LegalDocumentModal
        open={termsOpen}
        onOpenChange={setTermsOpen}
        i18nKey="termsOfUse"
        testIdPrefix="terms"
      />
      <LegalDocumentModal
        open={privacyOpen}
        onOpenChange={setPrivacyOpen}
        i18nKey="privacyPolicy"
        testIdPrefix="privacy"
      />
    </div>
  );
};
